import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Filter, Interweave } from "interweave";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import { useDeviceContext } from "src/context/device-context";
import useStyleSheet from "src/hooks/useStyleSheet";
import { HEADING_ID_PREFIX } from "src/pages/loaders/HomePageLoader";
import classes from "./FlightBookingGuide.module.css";
import { ReadingProgress } from "./ReadingProgress";
import { useLocaleContext } from "src/context/locale-context";

class EmptyTagFilter extends Filter {
  node(name, node) {
    if (node.textContent.trim() === "") {
      return null;
    }

    return node;
  }
}

class LinkFilter extends Filter {
  node(name, node) {
    if (name === "a") {
      node.setAttribute("target", "_blank");
    }

    return node;
  }
}

class HeadingFilter extends Filter {
  constructor() {
    super();
    this.headingCount = 1;
  }

  node(name, node) {
    if (name === "h4") {
      node.setAttribute("id", HEADING_ID_PREFIX + this.headingCount);
      node.textContent = node.textContent.trim();
      this.headingCount++;
    }

    return node;
  }
}

class ClassNameFilter extends Filter {
  node(name, node) {
    if (name === "ul" && node.classList.contains("airport-list")) {
      node.removeAttribute("class");
      node.classList.add("airline-list");
    } else if (name === "li") {
      if (
        ["airport-list", "benefits-list", "airline-list"].some((cls) =>
          node.parentElement.classList.contains(cls)
        )
      ) {
        node.removeAttribute("class");
      }
    }

    return node;
  }
}

function unwrapContentsContainer(node, children) {
  if (node.classList.contains("contents-container")) {
    if (children.length > 0) {
      return <>{children}</>;
    } else {
      return null;
    }
  }
}

export function FlightBookingGuide({ data }) {
  useStyleSheet(data.css);
  const { isMobile } = useDeviceContext();
  const { getStringRes } = useLocaleContext();
  const { hash } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    const idx = data.milestones.findIndex((m) => m.anchor === hash.slice(1));
    return idx === -1 ? 0 : idx;
  });

  const getStep = useCallback(
    (hid, scrollDirection = "down") => {
      let idx = data.milestones.findIndex((m) => m.anchor === hid);
      if (idx === -1) return;

      idx = scrollDirection === "down" ? idx : idx - 1;
      if (idx === -1) idx = 0;

      return idx;
    },
    [data.milestones]
  );

  useEffect(() => {
    if (isMobile) return;
    const winHeight = window.innerHeight;
    const topOffset = isMobile ? -(winHeight - 60) : -(winHeight - 300);
    const bottomOffset = isMobile ? -40 : -200;
    const options = {
      rootMargin: `${topOffset}px 0px ${bottomOffset}px 0px`,
      threshold: isMobile ? 0 : 1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target.prevScroll === undefined) {
          entry.target.prevScroll = 0;
        }
        const scrollDirection = window.scrollY > entry.target.prevScroll ? "down" : "up";
        entry.target.prevScroll = window.scrollY;

        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          const entryHeading =
            entry.target.tagName === "H4" ? entry.target : entry.target.previousElementSibling;
          setCurrentStep(getStep(entryHeading?.id, scrollDirection));
        }
      });
    }, options);

    const headings = document.querySelectorAll("h4[id]");
    const intersectionElements = Array.from(headings).map(
      (el, i, arr) => (i === arr.length - 1 ? el : el.nextElementSibling) || el
    );

    intersectionElements.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [isMobile, getStep]);

  return (
    <PaddedView>
      <div className={`${classes.container} ${isMobile ? classes.mobile : classes.desktop}`}>
        {/* <h2>Find the best deals for cheap flights </h2> */}
        <div
          className={`${classes["flight-booking-guide"]} ${
            isExpanded ? "" : classes.collapsed
          }`}>
          <Interweave
            noWrap
            content={data.html}
            transform={unwrapContentsContainer}
            filters={[
              new EmptyTagFilter(),
              new HeadingFilter(),
              new ClassNameFilter(),
              new LinkFilter(),
            ]}></Interweave>
          <ReadingProgress step={currentStep} milestones={data.milestones} />
        </div>
        <button
          className={classes["toggle-button"]}
          onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          {isExpanded ? getStringRes("common.collapse") : getStringRes("common.expand")}
        </button>
      </div>
    </PaddedView>
  );
}
